/**
 * GameOverView - Inline component for puzzle completion state.
 * 
 * This is a props-based component that renders INLINE within Daily.tsx
 * instead of being a separate route. This is proper app behavior.
 * 
 * States: Celebration → View Board (victory lap with solution replay)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Status } from '@daily-puzzle/shared';
import { createEngine } from '@daily-puzzle/rules';
import { Board } from '../Board';
import { InstrumentHeader } from '../InstrumentHeader';
import { Button } from '../ui';
import { ApiError, revealPuzzle } from '../../api/client';
import { CrownIcon, Miss1Icon, Miss2Icon, FailIcon } from './ResultIcons';
import { StreakBadge } from './StreakBadge';
import { trackShareClicked } from '../../lib/analytics';
import './GameOverView.css';

export interface GameOverViewProps {
    status: Status;
    attemptsUsed: number;
    puzzleDate: string;
    fen: string;
    finalFen?: string;
    orientation: 'white' | 'black';
    totalSteps: number;
    attacker: 'w' | 'b';
    shareText: string;
    /** Current effective daily date returned by the API */
    effectiveDailyDate: string;
    /** Current win streak (only for solved puzzles) */
    streak?: number;
    /** Solve time in seconds (only for solved puzzles with timestamps) */
    solveTimeSeconds?: number;
    /** Called when midnight crosses and we need to load fresh puzzle */
    onNewDay: () => void;
}

/** Returns the appropriate inline SVG icon component based on status and attempts */
function ResultIcon({ status, attemptsUsed }: { status: Status; attemptsUsed: number }) {
    if (status === Status.Solved && attemptsUsed === 0) {
        return <CrownIcon />;
    }
    if (status === Status.Solved && attemptsUsed === 1) {
        return <Miss1Icon />;
    }
    if (status === Status.Solved && attemptsUsed === 2) {
        return <Miss2Icon />;
    }
    // Status.Missed or fallback
    return <FailIcon />;
}

/**
 * Format seconds into MM:SS (or HH:MM:SS for 60min+) with leading zeros.
 * Examples: 47 → "00:47", 137 → "02:17", 3661 → "1:01:01"
 */
export function formatSolveTime(seconds: number): string {
    if (seconds < 0) return '00:00';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
        return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Animate a number from 0 to target over ~1.2s with ease-out deceleration.
 * Returns the current animated value as a formatted time string.
 */
function useCountUp(target: number | undefined, durationMs = 1200): string | null {
    const [current, setCurrent] = useState(0);
    const startRef = useRef<number | null>(null);
    const rafRef = useRef<number>(0);

    useEffect(() => {
        if (target === undefined || target <= 0) return;

        const animate = (timestamp: number) => {
            if (startRef.current === null) startRef.current = timestamp;
            const elapsed = timestamp - startRef.current;
            const progress = Math.min(elapsed / durationMs, 1);
            // Ease-out cubic: decelerates toward the end
            const eased = 1 - Math.pow(1 - progress, 3);
            setCurrent(Math.round(eased * target));

            if (progress < 1) {
                rafRef.current = requestAnimationFrame(animate);
            }
        };

        rafRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafRef.current);
    }, [target, durationMs]);

    if (target === undefined) return null;
    return formatSolveTime(current);
}

// Content for each state
function getContent(
    status: Status,
    attemptsUsed: number,
    nextPuzzleMessage: string,
    solveTimeSeconds?: number
) {
    if (status === Status.Solved) {
        const timeStr = solveTimeSeconds !== undefined ? formatSolveTime(solveTimeSeconds) : null;
        const subtitle = timeStr
            ? `Solved in ${timeStr}. ${nextPuzzleMessage}`
            : nextPuzzleMessage;

        if (attemptsUsed === 0) {
            return {
                title: 'Perfect!',
                subtitle,
                countdown: '',
            };
        }
        return {
            title: 'Checkmate',
            subtitle,
            countdown: '',
        };
    }

    if (status === Status.Missed) {
        return {
            title: 'Not this time',
            subtitle: `You'll get the next one. ${nextPuzzleMessage}`,
            countdown: '',
        };
    }

    if (status === Status.Revealed) {
        return {
            title: 'Solution',
            subtitle: '',
            countdown: '',
        };
    }

    return {
        title: '',
        subtitle: '',
        countdown: '',
    };
}

export function formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function getLocalDateString(): string {
    return formatLocalDate(new Date());
}

function parseCalendarDate(date: string): Date {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1, 0, 0, 0, 0));
}

export function addCalendarDays(date: string, days: number): string {
    const value = parseCalendarDate(date);
    value.setUTCDate(value.getUTCDate() + days);
    return value.toISOString().slice(0, 10);
}

function calendarDayDifference(from: string, to: string): number {
    const diffMs = parseCalendarDate(to).getTime() - parseCalendarDate(from).getTime();
    return Math.max(0, Math.round(diffMs / (24 * 60 * 60 * 1000)));
}

function parseLocalDateMidnight(date: string): Date {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1, 0, 0, 0, 0);
}

export function getNextPuzzleAvailability(currentLocalDate: string, effectiveDailyDate: string): {
    targetDate: string;
    daysAway: number;
    message: string;
} {
    const targetDate = addCalendarDays(effectiveDailyDate, 1);
    const daysAway = calendarDayDifference(currentLocalDate, targetDate);

    if (daysAway <= 0) {
        return {
            targetDate,
            daysAway: 0,
            message: 'A new puzzle is ready.',
        };
    }

    if (daysAway === 1) {
        return {
            targetDate,
            daysAway,
            message: 'See you tomorrow.',
        };
    }

    return {
        targetDate,
        daysAway,
        message: `Next puzzle unlocks in ${daysAway} days.`,
    };
}

// Calculate time until the next locally-available puzzle date.
function getTimeUntilLocalDate(targetDate: string): string {
    const now = new Date();
    const target = parseLocalDateMidnight(targetDate);

    const diff = Math.max(0, target.getTime() - now.getTime());
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

export function GameOverView({
    status: initialStatus,
    attemptsUsed,
    puzzleDate,
    fen: startFen,
    finalFen,
    orientation,
    totalSteps,
    attacker,
    shareText: _shareText,
    effectiveDailyDate,
    streak,
    solveTimeSeconds,
    onNewDay,
}: GameOverViewProps) {
    // Local status state (for revealing solution)
    const [status, setStatus] = useState(initialStatus);

    // View state: 'celebration' (initial) or 'board' (victory lap)
    const [view, setView] = useState<'celebration' | 'board'>('celebration');
    const initialAvailability = getNextPuzzleAvailability(getLocalDateString(), effectiveDailyDate);
    const [countdown, setCountdown] = useState(getTimeUntilLocalDate(initialAvailability.targetDate));
    const [shareConfirm, setShareConfirm] = useState(false);

    // Solution replay state
    const [solution, setSolution] = useState<string[] | null>(null);
    const [replayIndex, setReplayIndex] = useState(-1);
    const [isReplaying, setIsReplaying] = useState(false);
    const [replayFen, setReplayFen] = useState<string | null>(null);
    const [lastMove, setLastMove] = useState<{ from: string; to: string } | null>(null);

    const availability = getNextPuzzleAvailability(getLocalDateString(), effectiveDailyDate);
    const content = getContent(status, attemptsUsed, availability.message, solveTimeSeconds);

    // Animate solve time counting up from 00:00
    const animatedTime = useCountUp(
        status === Status.Solved ? solveTimeSeconds : undefined
    );

    // Update countdown every second until the next locally-available puzzle date.
    useEffect(() => {
        const syncCountdown = () => {
            if (getLocalDateString() >= availability.targetDate) {
                onNewDay();
                return true;
            }

            setCountdown(getTimeUntilLocalDate(availability.targetDate));
            return false;
        };

        if (syncCountdown()) {
            return;
        }

        const interval = setInterval(() => {
            if (syncCountdown()) {
                clearInterval(interval);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [availability.targetDate, onNewDay]);

    // Fetch solution when viewing board OR when status is Revealed
    useEffect(() => {
        const shouldFetch = (view === 'board' || status === Status.Revealed) && !solution && puzzleDate;
        if (shouldFetch) {
            const fetchSolution = async () => {
                try {
                    const idempotencyKey = crypto.randomUUID();
                    const response = await revealPuzzle(puzzleDate, idempotencyKey);
                    setSolution(response.solution_uci);
                } catch (error) {
                    if (isDayRolloverError(error)) {
                        onNewDay();
                        return;
                    }
                    console.error('Failed to fetch solution:', error);
                }
            };
            fetchSolution();
        }
    }, [view, status, solution, puzzleDate, onNewDay]);

    // Auto-start replay when solution is loaded
    useEffect(() => {
        if (solution && solution.length > 0) {
            setIsReplaying(true);
        }
    }, [solution]);

    // Replay animation effect
    useEffect(() => {
        if (!isReplaying || !solution || !startFen) return;

        const playMove = (index: number) => {
            if (index >= solution.length) {
                // Replay complete
                setIsReplaying(false);
                setReplayIndex(-1);
                // Show final position
                setReplayFen(finalFen ?? null);
                setLastMove(null);
                return;
            }

            // Compute FEN after moves up to this point
            const engine = createEngine(startFen);
            for (let i = 0; i <= index; i++) {
                const move = solution[i];
                if (move) {
                    engine.makeMove(move);
                }
            }

            const currentMove = solution[index];
            if (currentMove) {
                setReplayFen(engine.fen());
                setLastMove({
                    from: currentMove.slice(0, 2),
                    to: currentMove.slice(2, 4),
                });
            }
            setReplayIndex(index);

            // Schedule next move
            setTimeout(() => playMove(index + 1), 800);
        };

        // Start replay from beginning
        setReplayFen(startFen);
        setLastMove(null);
        setTimeout(() => playMove(0), 500);
    }, [isReplaying, solution, startFen, finalFen]);

    const handleViewBoard = () => {
        setView('board');
    };

    const handleSeeSolution = () => {
        setStatus(Status.Revealed);
    };

    const handleShare = async () => {
        const shareUrl = 'https://sendochess.pages.dev';
        const shareTitle = 'Sendo - Daily Chess Puzzle';

        // Try native share panel first
        if (navigator.share) {
            try {
                await navigator.share({
                    title: shareTitle,
                    url: shareUrl,
                });
                trackShareClicked(puzzleDate, 'native');
                return;
            } catch (error) {
                if ((error as Error).name !== 'AbortError') {
                    console.error('Share failed:', error);
                }
            }
        }

        // Fallback: copy URL to clipboard
        try {
            await navigator.clipboard.writeText(shareUrl);
            trackShareClicked(puzzleDate, 'clipboard');
            setShareConfirm(true);
            setTimeout(() => setShareConfirm(false), 2000);
        } catch {
            console.error('Failed to copy');
        }
    };

    const handleReplay = useCallback(() => {
        if (!solution || isReplaying) return;
        setIsReplaying(true);
    }, [solution, isReplaying]);

    // Determine which FEN to show on board
    const displayFen = replayFen ?? finalFen ?? startFen;

    // Board view (victory lap) - shows final position with solution replay
    if (view === 'board' && displayFen) {
        return (
            <div className="gameover-view">
                <div className="gameover-board-view">
                    <InstrumentHeader
                        puzzleDate={puzzleDate}
                        attacker={attacker}
                        currentStep={replayIndex >= 0 ? Math.floor((replayIndex + 1) / 2) + 1 : totalSteps}
                        totalSteps={totalSteps}
                        attemptsUsed={attemptsUsed}
                        status={status === Status.Solved ? 'solved' : 'missed'}
                    />

                    <Board
                        fen={displayFen}
                        orientation={orientation}
                        onMove={() => { }}
                        lastMove={lastMove ?? undefined}
                        disabled={true}
                    />

                    <div className="board-view-buttons">
                        <Button
                            className="gameover-button"
                            width="fixed"
                            onClick={handleReplay}
                            disabled={!solution || isReplaying}
                        >
                            {isReplaying ? 'Playing...' : 'Replay'}
                        </Button>
                        <Button
                            className="gameover-button"
                            variant="secondary"
                            width="fixed"
                            onClick={handleShare}
                        >
                            {shareConfirm ? 'Copied!' : 'Share'}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Solution view (for missed puzzles) - shows starting position
    if (status === Status.Revealed && startFen) {
        return (
            <div className="gameover-view">
                <div className="gameover-solution">
                    <InstrumentHeader
                        puzzleDate={puzzleDate}
                        attacker={attacker}
                        currentStep={0}
                        totalSteps={totalSteps}
                        attemptsUsed={attemptsUsed}
                        status="revealed"
                    />

                    <Board
                        fen={displayFen ?? startFen}
                        orientation={orientation}
                        onMove={() => { }}
                        lastMove={lastMove ?? undefined}
                        disabled={true}
                    />

                    <div className="solution-buttons">
                        <Button
                            className="gameover-button"
                            width="fixed"
                            onClick={handleReplay}
                            disabled={!solution || isReplaying}
                        >
                            {isReplaying ? 'Playing...' : 'Replay'}
                        </Button>
                        <Button
                            className="gameover-button"
                            variant="secondary"
                            width="fixed"
                            onClick={handleShare}
                        >
                            {shareConfirm ? 'Copied!' : 'Share'}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Celebration view (initial view)
    return (
        <div className="gameover-view">
            <div className="gameover-brand">Sendo</div>

            <div className="gameover-content">
                <div className="gameover-icon">
                    {status === Status.Solved && streak !== undefined ? (
                        <>
                            <StreakBadge streak={streak} />
                            {/* Show miss indicators below streak badge when solved with misses */}
                            {attemptsUsed === 1 && <Miss1Icon className="gameover-miss-indicator" />}
                            {attemptsUsed === 2 && <Miss2Icon className="gameover-miss-indicator" />}
                        </>
                    ) : (
                        <ResultIcon status={status} attemptsUsed={attemptsUsed} />
                    )}
                </div>

                <h1 className="gameover-title">{content.title}</h1>
                <p className="gameover-subtitle">
                    {animatedTime
                        ? `Solved in ${animatedTime}. ${availability.message}`
                        : content.subtitle}
                </p>
                {status !== Status.Revealed && (
                    <p className="gameover-countdown">Next puzzle in {countdown}</p>
                )}

                <div className="gameover-buttons">
                    {status === Status.Missed ? (
                        <>
                            <Button
                                className="gameover-button"
                                width="fixed"
                                onClick={handleSeeSolution}
                            >
                                See solution
                            </Button>
                            <Button
                                className="gameover-button"
                                variant="secondary"
                                width="fixed"
                                onClick={handleShare}
                            >
                                {shareConfirm ? 'Copied!' : 'Share'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                className="gameover-button"
                                width="fixed"
                                onClick={handleViewBoard}
                            >
                                View board
                            </Button>
                            <Button
                                className="gameover-button"
                                variant="secondary"
                                width="fixed"
                                onClick={handleShare}
                            >
                                {shareConfirm ? 'Copied!' : 'Share'}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function isDayRolloverError(error: unknown): boolean {
    return error instanceof ApiError && (
        error.code === 'read_only_day' || error.code === 'future_date'
    );
}
